import { Component, inject, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';
import { dataFromState, initialState } from '@libs/core/lib/state/async-states';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { withGetPublicOrder } from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import {
  withCreatePaymentIntent,
  withExchangeRate,
  withPaymentIntentConfirm,
} from '@libs/mtjr/shared/generated/entities/payment-intent/payment-intent-store-features';
import { Enums } from '@libs/mtjr/shared/generated/types';
import { PaymentMethodSelectorComponent } from '@libs/mtjr/shared/shared/payment-method-selector.component';
import { patchState, signalStore, withComputed, withMethods } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

const PublicCheckoutStore = signalStore(
  withGetPublicOrder(),
  withCreatePaymentIntent(),
  withExchangeRate(),
  withPaymentIntentConfirm(),
  withComputed((store) => ({
    usdExchangeRate: dataFromState(store.exchangeRateState, (data) => data?.usd),
    order: dataFromState(store.getPublicOrderState, (data) => data),
  })),
  withMethods((store) => ({
    resetPaymentState() {
      patchState(store, { createPaymentIntentState: initialState() });
    },
  })),
);

@Component({
  selector: 'mac-public-checkout',
  standalone: true,
  providers: [PublicCheckoutStore],
  imports: [
    PaymentMethodSelectorComponent,
    IonContent,
    IonToolbar,
    IonHeader,
    IonText,
    IonTitle,
    TranslatePipe,
    VxErrorComponent,
    VxCurrencyPipe,
    IonCard,
    IonButton,
    IonButtons,
    IonBackButton,
    IonIcon,
    VxErrorBarComponent,
    IonSpinner,
  ],
  templateUrl: './public-checkout.page.html',
})
export class PublicCheckoutPage implements OnDestroy {
  private route = inject(ActivatedRoute);
  private alerts = inject(AppAlerts);
  private modalCtrl = inject(ModalController);

  checkoutStore = inject(PublicCheckoutStore);

  // Order ID from route
  orderId = signal<string | undefined>(undefined);

  // State
  selectedPaymentMethod = signal<Enums.TopUpPaymentMethod | undefined>(undefined);
  valueInUSD = signal(0);
  isLoading = signal(false);

  routeSub?: Subscription;

  constructor() {
    // Load order from route path params
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const orderId = params.get('orderId');
      if (orderId) {
        this.orderId.set(orderId);
        this.checkoutStore.getPublicOrder({ orderId });
      }
    });
  }

  async ngOnDestroy() {
    this.routeSub?.unsubscribe();
    this.checkoutStore.resetPaymentState();
    await this.modalCtrl.dismiss();
  }
}
